import {MELEE_TEXTURE_KEYS} from '../../configs/meleeAnimation';

/**
 * Текстуры эффектов ближнего боя: мягкое свечение (искры, ореол вспышки),
 * звезда вспышки попадания, капля и пятно крови (их берет и выстрел). Картинок для них в public нет — пиксели считаются
 * кодом и регистрируются через textures.addUint8Array (почему не canvas и
 * почему цвет не умножен на альфу — см. shotProjectileTextures).
 *
 * Обе текстуры БЕЛЫЕ: в игре они красятся в цвет игрока (tint умножает цвет
 * текстуры на цвет игрока), форма задана только прозрачностью.
 */

export const MELEE_TEXTURE_SIZES = {
  [MELEE_TEXTURE_KEYS.GLOW]: {width: 64, height: 64},
  [MELEE_TEXTURE_KEYS.BURST]: {width: 128, height: 128},
  [MELEE_TEXTURE_KEYS.BLOOD_DROP]: {width: 32, height: 32},
  [MELEE_TEXTURE_KEYS.BLOOD_STAIN]: {width: 128, height: 128},
};

const clamp01 = (value) => Math.min(1, Math.max(0, value));

// Запись белого пикселя с заданной непрозрачностью 0..1
const putWhitePixel = (pixels, width, x, y, alpha) => {
  const offset = (y * width + x) * 4;
  pixels[offset] = 255;
  pixels[offset + 1] = 255;
  pixels[offset + 2] = 255;
  pixels[offset + 3] = Math.round(255 * clamp01(alpha));
};

/** Мягкое круглое свечение: яркое в центре, плавно гаснет к краю */
export const buildGlowPixels = () => {
  const {width, height} = MELEE_TEXTURE_SIZES[MELEE_TEXTURE_KEYS.GLOW];
  const pixels = new Uint8Array(width * height * 4);
  const centerX = (width - 1) / 2;
  const centerY = (height - 1) / 2;
  const radius = width / 2;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const distance = Math.hypot(x - centerX, y - centerY) / radius;
      if (distance >= 1) {
        continue;
      }
      putWhitePixel(pixels, width, x, y, (1 - distance) * (1 - distance));
    }
  }

  return pixels;
};

/**
 * Звезда вспышки: плотное ядро и восемь лучей — четыре длинных и четыре
 * покороче между ними. Края лучей и ядра мягкие: вспышка разрастается и
 * гаснет за четверть секунды, жесткая кромка в ней читалась бы как значок
 */
export const buildBurstPixels = () => {
  const {width, height} = MELEE_TEXTURE_SIZES[MELEE_TEXTURE_KEYS.BURST];
  const pixels = new Uint8Array(width * height * 4);
  const centerX = (width - 1) / 2;
  const centerY = (height - 1) / 2;
  const radius = width / 2;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const dx = (x - centerX) / radius;
      const dy = (y - centerY) / radius;
      const distance = Math.hypot(dx, dy);
      if (distance >= 1) {
        continue;
      }
      const angle = Math.atan2(dy, dx);
      // Ядро: плотное до трети радиуса
      const core = clamp01(1 - distance / 0.42);
      // Длинные лучи — по осям, короткие — по диагоналям
      const longRays = Math.pow(Math.abs(Math.cos(angle * 2)), 26) * clamp01(1 - distance);
      const shortRays = Math.pow(Math.abs(Math.sin(angle * 2)), 26) * clamp01(1 - distance / 0.68);
      // Общий ореол, чтобы лучи не висели в пустоте
      const halo = 0.35 * (1 - distance) * (1 - distance);
      putWhitePixel(pixels, width, x, y, core * core + longRays + 0.8 * shortRays + halo);
    }
  }

  return pixels;
};

/** Капля крови: плотный круг с мягким краем в полтора пикселя */
export const buildBloodDropPixels = () => {
  const {width, height} = MELEE_TEXTURE_SIZES[MELEE_TEXTURE_KEYS.BLOOD_DROP];
  const pixels = new Uint8Array(width * height * 4);
  const centerX = (width - 1) / 2;
  const centerY = (height - 1) / 2;
  const radius = width / 2 - 2;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const distance = Math.hypot(x - centerX, y - centerY);
      putWhitePixel(pixels, width, x, y, clamp01((radius - distance) / 1.5 + 0.5));
    }
  }

  return pixels;
};

/**
 * Пятно крови: неровная клякса (край колышется по углу) и россыпь мелких
 * брызг вокруг. Узор задан формулой, а не случайностью: текстура одна на все
 * пятна, разнообразие дают поворот и размер
 */
export const buildBloodStainPixels = () => {
  const {width, height} = MELEE_TEXTURE_SIZES[MELEE_TEXTURE_KEYS.BLOOD_STAIN];
  const pixels = new Uint8Array(width * height * 4);
  const centerX = (width - 1) / 2;
  const centerY = (height - 1) / 2;
  const radius = width / 2;
  // Брызги вокруг кляксы: угол, удаление (доля радиуса), размер (доля радиуса)
  const specks = [
    [0.3, 0.78, 0.09], [1.4, 0.7, 0.07], [2.2, 0.84, 0.06], [2.9, 0.66, 0.1],
    [3.8, 0.8, 0.08], [4.6, 0.72, 0.06], [5.5, 0.86, 0.07], [6.0, 0.62, 0.05],
  ];

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const dx = (x - centerX) / radius;
      const dy = (y - centerY) / radius;
      const distance = Math.hypot(dx, dy);
      const angle = Math.atan2(dy, dx);
      // Край кляксы: около 0.42 радиуса, с выступами
      const edge = 0.42 + 0.07 * Math.sin(angle * 3 + 0.5) + 0.05 * Math.sin(angle * 7 + 1.3) + 0.03 * Math.sin(angle * 13);
      let alpha = clamp01((edge - distance) * radius / 1.5 + 0.5);
      specks.forEach(([speckAngle, speckDistance, speckSize]) => {
        const sx = Math.cos(speckAngle) * speckDistance;
        const sy = Math.sin(speckAngle) * speckDistance;
        const speck = clamp01((speckSize - Math.hypot(dx - sx, dy - sy)) * radius / 1.5 + 0.5);
        alpha = Math.max(alpha, speck);
      });
      if (distance >= 0.98) {
        alpha = 0;
      }
      putWhitePixel(pixels, width, x, y, alpha);
    }
  }

  return pixels;
};

const PIXEL_BUILDERS = {
  [MELEE_TEXTURE_KEYS.GLOW]: buildGlowPixels,
  [MELEE_TEXTURE_KEYS.BURST]: buildBurstPixels,
  [MELEE_TEXTURE_KEYS.BLOOD_DROP]: buildBloodDropPixels,
  [MELEE_TEXTURE_KEYS.BLOOD_STAIN]: buildBloodStainPixels,
};

/**
 * Зарегистрировать текстуры эффектов боя в сцене (один раз: готовые не трогает).
 *
 * @param {Phaser.Scene} scene
 * @param {Object} [options]
 * @param {number} [options.linearFilter] — режим фильтрации LINEAR: у текстуры
 *   из массива по умолчанию NEAREST, и растянутая вспышка пошла бы квадратами
 * @returns {boolean} все ли текстуры на месте; false — анимации не будет
 */
export const ensureMeleeTextures = (scene, {linearFilter} = {}) => {
  const textures = scene?.textures;
  if (!textures?.exists || !textures.addUint8Array) {
    return false;
  }

  return Object.values(MELEE_TEXTURE_KEYS).every((key) => {
    if (textures.exists(key)) {
      return true;
    }
    const {width, height} = MELEE_TEXTURE_SIZES[key];
    const texture = textures.addUint8Array(key, PIXEL_BUILDERS[key](), width, height);
    if (!texture) {
      return false;
    }
    if (linearFilter !== undefined) {
      texture.setFilter?.(linearFilter);
    }
    return true;
  });
};
